import { useMemo, useState, useEffect } from "react";
import { Stack, IconButton } from "@fluentui/react";
import DOMPurify from "dompurify";

import styles from "./Answer.module.css";

import { useMsal } from "@azure/msal-react";
import { ChatAppResponse, getCitationFilePath, updateLogEntryLikeApi } from "../../api";
import { parseAnswerToHtml } from "./AnswerParser";
import { AnswerIcon } from "./AnswerIcon";
import { useLogin, getToken, isLoggedIn, requireAccessControl } from "../../authConfig";

interface Props {
    answer: ChatAppResponse;
    isSelected?: boolean;
    isStreaming: boolean;
    onCitationClicked: (filePath: string) => void;
    onThoughtProcessClicked: () => void;
    onSupportingContentClicked: () => void;
    onFollowupQuestionClicked?: (question: string) => void;
    showFollowupQuestions?: boolean;
}

// isAdmin im SessionStorage speichern
const setIsAdmin = (isAdmin: boolean) => {
    sessionStorage.setItem("isAdmin", isAdmin.toString());
};
// isAdmin aus dem SessionStorage abrufen
const getIsAdmin = () => {
    const storedIsAdmin = sessionStorage.getItem("isAdmin");
    return storedIsAdmin === "true"; // Konvertiere den String zurück in einen Boolean
};

export const Answer = ({
    answer,
    isSelected,
    isStreaming,
    onCitationClicked,
    onThoughtProcessClicked,
    onSupportingContentClicked,
    onFollowupQuestionClicked,
    showFollowupQuestions
}: Props) => {
    const [isAdmin, setIsAdmin] = useState(getIsAdmin()); // Initialisiere den Zustand mit dem Wert aus dem SessionStorage

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const adminParam = searchParams.get("admin");
        if (adminParam && adminParam.toLowerCase() === "true") {
            setIsAdmin(true);
        } else {
            setIsAdmin(false);
        }
        sessionStorage.setItem("isAdmin", isAdmin.toString());
    }, [isAdmin]);

    const followupQuestions = answer.choices[0].context.followup_questions;
    const messageContent = answer.choices[0].message.content;

    const parsedAnswer = useMemo(() => parseAnswerToHtml(messageContent, isStreaming, isAdmin, onCitationClicked), [answer]);

    const sanitizedAnswerHtml = DOMPurify.sanitize(parsedAnswer.answerHtml);

    const [liked, setLiked] = useState<boolean>(false); // Initialize liked state with TypeScript type annotation

    const client = useLogin ? useMsal().instance : undefined;

    const toggleLike = async (): Promise<void> => {
        setLiked(!liked); // Set liked to true when the like button is clicked
        setDisliked(false); // Ensure disliked is false when liked is true

        const token = client ? await getToken(client) : undefined;
        const bla = updateLogEntryLikeApi({ message: "" + liked }, token)
            .then(response => console.log("Log entry (like) update successful", response))
            .catch(error => console.error("Failed to update log entry (like)", error));
    };

    const [disliked, setDisliked] = useState<boolean>(false); // Initialize disliked state with TypeScript type annotation

    const toggleDislike = async (): Promise<void> => {
        setDisliked(!disliked); // Set disliked to true when the dislike button is clicked
        setLiked(false); // Ensure liked is false when disliked is true

        const token = client ? await getToken(client) : undefined;
        const bla = updateLogEntryLikeApi({ message: "" + disliked }, token)
            .then(response => console.log("Log entry (like) update successful", response))
            .catch(error => console.error("Failed to update log entry (like)", error));
    };

    return (
        <Stack className={`${styles.answerContainer} ${isSelected && styles.selected}`} verticalAlign="space-between">
            <Stack.Item>
                <Stack horizontal horizontalAlign="space-between">
                    <AnswerIcon />
                    <div>
                        <IconButton
                            style={{ color: liked ? "blue" : "black" }} // Conditional style based on liked state
                            iconProps={{ iconName: "LikeSolid" }}
                            title="Like"
                            ariaLabel="Like"
                            onClick={toggleLike} // Handler to toggle state
                        />
                        <IconButton
                            style={{ color: disliked ? "blue" : "black" }}
                            iconProps={{ iconName: "DislikeSolid" }}
                            title="Dislike"
                            ariaLabel="Dislike"
                            onClick={toggleDislike} // Handler to toggle state
                            /*<--onClick={() => onThoughtProcessClicked()}
                            disabled={!answer.choices[0].context.thoughts?.length} -->*/
                        />
                        {isAdmin && (
                            <IconButton
                                style={{ color: "black" }}
                                iconProps={{ iconName: "Lightbulb" }}
                                title="Show thought process"
                                ariaLabel="Show thought process"
                                onClick={() => onThoughtProcessClicked()}
                                disabled={!answer.choices[0].context.thoughts?.length}
                            />
                        )}
                        {isAdmin && (
                            <IconButton
                                style={{ color: "black" }}
                                iconProps={{ iconName: "ClipboardList" }}
                                title="Show supporting content"
                                ariaLabel="Show supporting content"
                                onClick={() => onSupportingContentClicked()}
                                disabled={!answer.choices[0].context.data_points}
                            />
                        )}
                    </div>
                </Stack>
            </Stack.Item>

            <Stack.Item grow>
                <div className={styles.answerText} dangerouslySetInnerHTML={{ __html: sanitizedAnswerHtml }}></div>
            </Stack.Item>

            {!!parsedAnswer.citations.length && (
                <Stack.Item>
                    <Stack horizontal wrap tokens={{ childrenGap: 5 }}>
                        <span className={styles.citationLearnMore}>Källa:</span>
                        {parsedAnswer.citations.map((x, i) => {
                            const path = getCitationFilePath(x);
                            return (
                                <a key={i} className={styles.citation} title={x} onClick={() => onCitationClicked(path)}>
                                    {`${++i}. ${x}`}
                                </a>
                            );
                        })}
                    </Stack>
                </Stack.Item>
            )}

            {!!followupQuestions?.length && showFollowupQuestions && onFollowupQuestionClicked && (
                <Stack.Item>
                    <Stack horizontal wrap className={`${!!parsedAnswer.citations.length ? styles.followupQuestionsList : ""}`} tokens={{ childrenGap: 6 }}>
                        <span className={styles.followupQuestionLearnMore}>Följdfrågor:</span>
                        {followupQuestions.map((x, i) => {
                            return (
                                <a key={i} className={styles.followupQuestion} title={x} onClick={() => onFollowupQuestionClicked(x)}>
                                    {`${x}`}
                                </a>
                            );
                        })}
                    </Stack>
                </Stack.Item>
            )}
        </Stack>
    );
};
